import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useApiTokensMutations from "../../datahooks/useApiTokensMutations";
import { ApiKeyNameSchema } from "../../schema";

import type { API_KEYS_ACTIONS } from "../../constants";
import type { ApiToken } from "../../types";

interface ApiKeyActionsProps {
  token: ApiToken;
  action: keyof typeof API_KEYS_ACTIONS;
  onClose: () => void;
  onBack: () => void;
}

function EditApiKey({
  token,
  onClose,
  onBack,
}: {
  token: ApiToken;
  onClose: () => void;
  onBack: () => void;
}) {
  const isDesktop = useTwBreakpoint("lg");

  const { name, id: tokenId } = token;
  const { updateApiToken, isUpdatingToken } = useApiTokensMutations();
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ name: string }>({
    defaultValues: { name },
    resolver: valibotResolver(ApiKeyNameSchema),
  });

  function onSubmit(data: { name: string }) {
    updateApiToken({ tokenId, updates: { name: data.name } }).then(() =>
      onClose(),
    );
  }

  return (
    <>
      <Header title="Edit API Key" />
      <form className="flex flex-col gap-y-8" onSubmit={handleSubmit(onSubmit)}>
        <Body className="gap-y-8">
          <FormField
            label="Key name"
            htmlFor="name"
            error={errors.name?.message}
          >
            <Input {...register("name")} id="name" />
          </FormField>
        </Body>
        <Footer className="justify-end">
          <Button
            variant="secondary-black"
            disabled={isUpdatingToken}
            onClick={onBack}
          >
            Cancel
          </Button>
          <Button isLoading={isUpdatingToken} type="submit">
            Save
          </Button>
        </Footer>
      </form>
    </>
  );
}

function DeleteApiKey({
  token,
  onClose,
  onBack,
}: {
  token: ApiToken;
  onClose: () => void;
  onBack: () => void;
}) {
  const isDesktop = useTwBreakpoint("lg");

  const { name, id } = token;
  const { deleteApiToken, isDeletingToken } = useApiTokensMutations();

  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  function onDelete() {
    deleteApiToken(id).then(() => onClose());
  }

  return (
    <>
      <Header
        title="Delete API Key?"
        subtitle={
          <>
            Are you sure you want to delete the API key named{" "}
            <span className="font-semibold text-black-950">{name}</span>? This
            action cannot be undone.
          </>
        }
      />
      <Footer className="justify-end">
        <Button
          variant="secondary-black"
          disabled={isDeletingToken}
          onClick={onBack}
        >
          Cancel
        </Button>
        <Button
          variant="outline-danger"
          isLoading={isDeletingToken}
          onClick={onDelete}
        >
          Delete
        </Button>
      </Footer>
    </>
  );
}

export default function ApiKeyActions({
  token,
  action,
  onClose,
  onBack,
}: ApiKeyActionsProps) {
  if (action === "edit") {
    return <EditApiKey token={token} onClose={onClose} onBack={onBack} />;
  }
  if (action === "remove") {
    return <DeleteApiKey token={token} onClose={onClose} onBack={onBack} />;
  }
}
